/* RO choices adapt the existing native Pack radio. That controller owns price, plans, media, URL and cart. */
(() => {
  if (customElements.get('cq-ro-pack-options')) return;
  class CQROPackOptions extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const lifecycle = this.abort;
      customElements.whenDefined('cq-purchase-options').then(() => {
        if (this.isConnected && this.abort === lifecycle && !lifecycle.signal.aborted) this.bind();
      });
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.setPending('');
      this.restoreFitGuide();
      this.querySelectorAll('cq-option-guide').forEach(guide => guide.hide?.());
      this.nativeGroup?.classList.remove('cq-ro-pack-native');
      this.hidden = true;
    }
    dockFitGuide() {
      const group = this.querySelector('[data-cq-ro-system-group]');
      if (!group || this.fitGuide?.parentElement === group) return;
      const guide = this.nativeGroup.querySelector(':scope > cq-option-guide[data-cq-guide="ro-pack-fit"]');
      if (!guide) return;
      this.fitGuide = guide;
      // Keep the original location for native fallback and section/editor disconnects.
      this.fitGuideAnchor = document.createComment('RO fit guide native location');
      guide.before(this.fitGuideAnchor);
      group.querySelector('legend').after(guide);
    }
    restoreFitGuide() {
      if (!this.fitGuide) return;
      this.fitGuide.hide?.();
      this.fitGuide.hidden = false;
      this.fitGuideAnchor?.replaceWith(this.fitGuide);
      this.fitGuide = null;
      this.fitGuideAnchor = null;
    }
    bind() {
      const picker = this.closest('cq-purchase-options');
      const form = document.getElementById(this.dataset.formId);
      const groups = picker?.querySelectorAll('[data-cq-option]');
      if (!form || !picker || groups.length !== 1) return;
      let rows;
      try { rows = JSON.parse(this.querySelector('[data-cq-ro-options-data]').textContent); } catch (_) { return; }
      const radios = [...groups[0].querySelectorAll('input[type="radio"]')];
      if (!rows.length || !rows.every(row => radios.some(input => input.value === row.option))) return;
      this.picker = picker;
      this.form = form;
      this.rows = rows;
      this.radios = radios;
      this.nativeGroup = groups[0];
      this.setup = this.setup || rows.find(row => row.type === 'prefilters')?.setup;
      const options = { signal: this.abort.signal };
      this.addEventListener('change', event => {
        if (!event.target.matches('[data-cq-ro-type], [data-cq-ro-system], [data-cq-ro-setup]')) return;
        event.stopPropagation();
        this.choose(event.target);
      }, options);
      this.addEventListener('click', event => {
        if (event.target.matches('[data-cq-ro-type][value="prefilters"]')) this.setPending('');
      }, options);
      form.addEventListener('submit', event => {
        if (!this.pendingType) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.querySelector('[data-cq-ro-system]')?.focus();
      }, { ...options, capture: true });
      picker.addEventListener('cq:variant-change', event => {
        if (event.target === picker) this.sync(event.detail?.variant?.id);
      }, options);
      picker.addEventListener('change', () => {
        // Purchase mode and cadence refresh buttons without changing the native variant.
        if (this.pendingType) this.pendingButtons?.forEach(([button]) => { button.disabled = true; });
      }, options);
      form.addEventListener('change', event => {
        if (event.target.name === 'id') this.sync(event.target.value);
      }, options);
      window.addEventListener('pageshow', () => this.sync(form.elements.id.value), options);
      this.sync(form.elements.id.value);
    }
    sync(id) {
      const row = this.rows.find(item => String(item.id) === String(id));
      if (!row) {
        this.setPending('');
        this.restoreFitGuide();
        this.querySelectorAll('cq-option-guide').forEach(guide => guide.hide?.());
        this.hidden = true;
        this.nativeGroup.classList.remove('cq-ro-pack-native');
        return;
      }
      if (this.current && this.current.id !== row.id) this.setPending('');
      this.current = row;
      // Several GPD choices share one variant. Never reconstruct a specific system from that shared ID.
      const restored = this.querySelector('[data-cq-ro-system]:checked')?.value;
      const remembered = this.system || restored;
      this.system = row.systems.includes(remembered) ? remembered : row.systems.length === 1 ? row.systems[0] : '';
      if (row.type === 'prefilters') this.setup = row.setup;
      const selectedType = this.pendingType || row.type;
      this.querySelectorAll('[data-cq-ro-type]').forEach(input => { input.checked = input.value === selectedType; });
      this.querySelectorAll('[data-cq-ro-system]').forEach(input => { input.checked = input.value === this.system; });
      this.querySelectorAll('[data-cq-ro-setup]').forEach(input => { input.checked = input.value === this.setup; });
      const complete = selectedType === 'complete';
      this.dockFitGuide();
      this.querySelector('[data-cq-ro-system-group]').hidden = !complete;
      if (this.fitGuide) {
        if (!complete) {
          const hadFocus = this.fitGuide.contains(document.activeElement);
          this.fitGuide.hide?.();
          if (hadFocus) this.querySelector('[data-cq-ro-type]:checked')?.focus();
        }
        // The shared dialog watches its own hidden attribute, not its ancestors.
        this.fitGuide.hidden = !complete;
      }
      const setupGroup = this.querySelector('[data-cq-ro-setup-group]');
      if (setupGroup) setupGroup.hidden = selectedType !== 'prefilters';
      this.querySelector('[data-cq-ro-config-note]').hidden = Boolean(this.system);
      this.querySelector('[data-cq-ro-membrane-check]').hidden = row.type !== 'complete' || !row.systems.includes('300');
      this.hidden = false;
      this.nativeGroup.classList.add('cq-ro-pack-native');
      if (this.pendingType) this.pendingButtons?.forEach(([button]) => { button.disabled = true; });
      this.dispatchEvent(new CustomEvent('cq:ro-selection-change', {
        bubbles: true,
        detail: { variantId: row.id, system: this.system, type: selectedType, setup: selectedType === 'prefilters' ? row.setup : '' }
      }));
    }
    setPending(type) {
      if (type && !this.pendingType) {
        this.pendingButtons = [...this.form.querySelectorAll('button[type="submit"]')].map(button => [button, button.disabled]);
        this.pendingButtons.forEach(([button]) => { button.disabled = true; });
      } else if (!type && this.pendingType) {
        this.pendingButtons?.forEach(([button, disabled]) => { button.disabled = disabled; });
        this.pendingButtons = null;
      }
      this.pendingType = type;
    }
    choose(input) {
      const type = input.matches('[data-cq-ro-type]') ? input.value
        : input.matches('[data-cq-ro-system]') ? this.pendingType || this.current.type : this.current.type;
      if (input.matches('[data-cq-ro-system]')) this.system = input.value;
      if (input.matches('[data-cq-ro-setup]')) this.setup = input.value;
      if (type === 'complete' && !this.system) {
        this.setPending('complete');
        this.sync(this.current.id);
        this.querySelector('[data-cq-ro-system]')?.focus();
        return;
      }
      this.setPending('');
      const row = this.rows.find(item => item.type === type
        && (!this.system || item.systems.includes(this.system))
        && (type === 'complete' || item.setup === this.setup));
      const native = row && this.radios.find(radio => radio.value === row.option);
      if (!native) { this.sync(this.current.id); return; }
      native.checked = true;
      // Do not write hidden id: the purchase controller must update the complete purchase state first.
      native.dispatchEvent(new Event('change', { bubbles: true }));
      this.sync(this.form.elements.id.value);
    }
  }
  customElements.define('cq-ro-pack-options', CQROPackOptions);
})();
